/**
 * Página de reportes del profesor
 */

import React, { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { alumnosProfesorService } from "../../../services/profesor";
import { cursosProfesorService } from "../../../services/profesor/cursos.service";
import { simulacionesProfesorService } from "../../../services/profesor/simulaciones.service";
import { AlumnoProfesor } from "../alumnos/dto/alumnos.dto";
import { ProfesorLayout } from "../components";
import { CursoProfesor } from "../cursos/dto/cursos.dto";
import { SimulacionProfesor } from "../simulaciones/dto/simulaciones.dto";

type TipoReporte =
  | "alumnos"
  | "cursos"
  | "simulaciones"
  | "progreso-general"
  | "rendimiento-alumno";

interface FiltrosReporte {
  fechaDesde?: string;
  fechaHasta?: string;
  cursoId?: number;
  alumnoId?: number;
}

export const ProfesorReportesPage: React.FC = () => {
  const { user } = useAuth();
  const [tipoReporte, setTipoReporte] = useState<TipoReporte>("alumnos");
  const [filtros, setFiltros] = useState<FiltrosReporte>({});
  const [alumnos, setAlumnos] = useState<AlumnoProfesor[]>([]);
  const [cursos, setCursos] = useState<CursoProfesor[]>([]);
  const [simulaciones, setSimulaciones] = useState<SimulacionProfesor[]>([]);
  const [loading, setLoading] = useState(false);
  const [generando, setGenerando] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [alumnosData, cursosData, simulacionesData] = await Promise.all([
        alumnosProfesorService.getAlumnos().catch(() => []),
        cursosProfesorService.getCursos().catch(() => []),
        simulacionesProfesorService.getSimulaciones().catch(() => []),
      ]);

      setAlumnos(alumnosData || []);
      setCursos(cursosData || []);
      setSimulaciones(simulacionesData || []);
    } catch (error) {
      console.error("Error cargando datos:", error);
    } finally {
      setLoading(false);
    }
  };

  const generarPDF = async () => {
    setGenerando(true);
    try {
      // Importar jsPDF dinámicamente
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF();

      // Configuración del documento
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 20;

      // Header con gradiente simulado
      doc.setFillColor(139, 92, 246); // purple-600
      doc.rect(0, 0, pageWidth, 50, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("Fracto Profesor", 20, 25);
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Reporte generado: ${new Date().toLocaleDateString("es-ES")}`,
        20,
        35
      );
      doc.text(`Profesor: ${user?.nombres} ${user?.apellidos}`, 20, 42);

      yPosition = 60;

      // Título del reporte
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      const titulos: Record<TipoReporte, string> = {
        alumnos: "Reporte de Alumnos",
        cursos: "Reporte de Cursos",
        simulaciones: "Reporte de Simulaciones",
        "progreso-general": "Reporte de Progreso General",
        "rendimiento-alumno": "Reporte de Rendimiento por Alumno",
      };
      doc.text(titulos[tipoReporte], 20, yPosition);
      yPosition += 15;

      // Contenido según el tipo de reporte
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      switch (tipoReporte) {
        case "alumnos":
          generarReporteAlumnos(doc, alumnos, yPosition);
          break;
        case "cursos":
          generarReporteCursos(doc, cursos, yPosition);
          break;
        case "simulaciones":
          generarReporteSimulaciones(doc, simulaciones, yPosition);
          break;
        case "progreso-general":
          generarReporteProgresoGeneral(
            doc,
            alumnos,
            cursos,
            simulaciones,
            yPosition
          );
          break;
        case "rendimiento-alumno":
          if (filtros.alumnoId) {
            const alumno = alumnos.find(
              (a) => a.idUsuario === filtros.alumnoId
            );
            if (alumno) {
              generarReporteRendimientoAlumno(
                doc,
                alumno,
                simulaciones.filter(
                  (s) => s.alumno.idUsuario === filtros.alumnoId
                ),
                yPosition
              );
            }
          }
          break;
      }

      // Guardar PDF
      doc.save(
        `reporte-${tipoReporte}-${new Date().toISOString().split("T")[0]}.pdf`
      );
    } catch (error) {
      console.error("Error generando PDF:", error);
      alert(
        "Error al generar el PDF. Por favor, instala jspdf: npm install jspdf"
      );
    } finally {
      setGenerando(false);
    }
  };

  const generarReporteAlumnos = (
    doc: any,
    alumnos: AlumnoProfesor[],
    startY: number
  ) => {
    let y = startY;
    doc.setFont("helvetica", "bold");
    doc.text("Resumen", 20, y);
    y += 10;
    doc.setFont("helvetica", "normal");
    doc.text(`Total de alumnos: ${alumnos.length}`, 20, y);
    y += 8;
    doc.text(
      `Alumnos vigentes: ${alumnos.filter((a) => a.vigente).length}`,
      20,
      y
    );
    y += 15;

    // Tabla de alumnos
    doc.setFont("helvetica", "bold");
    doc.text("Lista de Alumnos", 20, y);
    y += 10;

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Nombre", 20, y);
    doc.text("Email", 80, y);
    doc.text("Cursos", 140, y);
    doc.text("Estado", 170, y);
    y += 5;

    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, 190, y);
    y += 5;

    doc.setFont("helvetica", "normal");
    alumnos.forEach((alumno) => {
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      doc.text(`${alumno.nombres} ${alumno.apellidos}`, 20, y);
      doc.text(alumno.email, 80, y);
      doc.text(
        `${alumno.cursos?.length || 0} curso${
          alumno.cursos?.length !== 1 ? "s" : ""
        }`,
        140,
        y
      );
      doc.text(alumno.vigente ? "Vigente" : "No vigente", 170, y);
      y += 7;
    });
  };

  const generarReporteCursos = (
    doc: any,
    cursos: CursoProfesor[],
    startY: number
  ) => {
    let y = startY;
    doc.setFont("helvetica", "bold");
    doc.text("Resumen", 20, y);
    y += 10;
    doc.setFont("helvetica", "normal");
    doc.text(`Total de cursos: ${cursos.length}`, 20, y);
    y += 8;
    doc.text(
      `Cursos vigentes: ${cursos.filter((c) => c.vigente).length}`,
      20,
      y
    );
    y += 15;

    // Tabla de cursos
    doc.setFont("helvetica", "bold");
    doc.text("Lista de Cursos", 20, y);
    y += 10;

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Curso", 20, y);
    doc.text("Colegio", 80, y);
    doc.text("Alumnos", 140, y);
    doc.text("Año", 160, y);
    doc.text("Estado", 175, y);
    y += 5;

    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, 190, y);
    y += 5;

    doc.setFont("helvetica", "normal");
    cursos.forEach((curso) => {
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      doc.text(curso.nombreCompleto, 20, y);
      doc.text(curso.colegio.nombre, 80, y);
      doc.text(curso.cantidadAlumnos.toString(), 140, y);
      doc.text(curso.anio.toString(), 160, y);
      doc.text(curso.vigente ? "Vigente" : "No vigente", 175, y);
      y += 7;
    });
  };

  const generarReporteSimulaciones = (
    doc: any,
    simulaciones: SimulacionProfesor[],
    startY: number
  ) => {
    let y = startY;
    doc.setFont("helvetica", "bold");
    doc.text("Resumen", 20, y);
    y += 10;
    doc.setFont("helvetica", "normal");
    doc.text(`Total de simulaciones: ${simulaciones.length}`, 20, y);
    y += 8;
    doc.text(
      `Simulaciones finalizadas: ${
        simulaciones.filter((s) => s.estado === "FINALIZADA").length
      }`,
      20,
      y
    );
    y += 15;

    // Tabla de simulaciones
    doc.setFont("helvetica", "bold");
    doc.text("Simulaciones", 20, y);
    y += 10;

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Alumno", 20, y);
    doc.text("Fecha", 80, y);
    doc.text("Estado", 120, y);
    doc.text("Preguntas", 150, y);
    doc.text("% Aciertos", 170, y);
    y += 5;

    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, 190, y);
    y += 5;

    doc.setFont("helvetica", "normal");
    simulaciones.slice(0, 30).forEach((sim) => {
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      doc.text(sim.alumno.nombreCompleto, 20, y);
      doc.text(new Date(sim.fecha).toLocaleDateString("es-ES"), 80, y);
      doc.text(sim.estado, 120, y);
      doc.text(sim.totalPreguntas.toString(), 150, y);
      doc.text(`${sim.porcentajeCorrectas.toFixed(1)}%`, 170, y);
      y += 7;
    });
  };

  const generarReporteProgresoGeneral = (
    doc: any,
    alumnos: AlumnoProfesor[],
    cursos: CursoProfesor[],
    simulaciones: SimulacionProfesor[],
    startY: number
  ) => {
    let y = startY;
    doc.setFont("helvetica", "bold");
    doc.text("Resumen General", 20, y);
    y += 10;
    doc.setFont("helvetica", "normal");
    doc.text(`Total de alumnos: ${alumnos.length}`, 20, y);
    y += 8;
    doc.text(`Total de cursos: ${cursos.length}`, 20, y);
    y += 8;
    doc.text(`Total de simulaciones: ${simulaciones.length}`, 20, y);
    y += 8;
    doc.text(
      `Simulaciones finalizadas: ${
        simulaciones.filter((s) => s.estado === "FINALIZADA").length
      }`,
      20,
      y
    );
    y += 15;

    // Estadísticas de simulaciones
    if (simulaciones.length > 0) {
      const finalizadas = simulaciones.filter((s) => s.estado === "FINALIZADA");
      if (finalizadas.length > 0) {
        const promedioAciertos =
          finalizadas.reduce((sum, s) => sum + s.porcentajeCorrectas, 0) /
          finalizadas.length;
        doc.setFont("helvetica", "bold");
        doc.text("Estadísticas de Rendimiento", 20, y);
        y += 10;
        doc.setFont("helvetica", "normal");
        doc.text(
          `Promedio de aciertos: ${promedioAciertos.toFixed(2)}%`,
          20,
          y
        );
      }
    }
  };

  const generarReporteRendimientoAlumno = (
    doc: any,
    alumno: AlumnoProfesor,
    simulaciones: SimulacionProfesor[],
    startY: number
  ) => {
    let y = startY;
    doc.setFont("helvetica", "bold");
    doc.text(`Reporte de Rendimiento: ${alumno.nombreCompleto}`, 20, y);
    y += 10;
    doc.setFont("helvetica", "normal");
    doc.text(`Email: ${alumno.email}`, 20, y);
    y += 8;
    doc.text(`Cursos asignados: ${alumno.cursos?.length || 0}`, 20, y);
    y += 15;

    if (simulaciones.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Simulaciones Realizadas", 20, y);
      y += 10;

      const finalizadas = simulaciones.filter((s) => s.estado === "FINALIZADA");
      if (finalizadas.length > 0) {
        const promedioAciertos =
          finalizadas.reduce((sum, s) => sum + s.porcentajeCorrectas, 0) /
          finalizadas.length;
        doc.setFont("helvetica", "normal");
        doc.text(`Total de simulaciones: ${simulaciones.length}`, 20, y);
        y += 8;
        doc.text(`Simulaciones finalizadas: ${finalizadas.length}`, 20, y);
        y += 8;
        doc.text(
          `Promedio de aciertos: ${promedioAciertos.toFixed(2)}%`,
          20,
          y
        );
        y += 15;

        // Tabla de simulaciones
        doc.setFont("helvetica", "bold");
        doc.text("Detalle de Simulaciones", 20, y);
        y += 10;

        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text("Fecha", 20, y);
        doc.text("Estado", 60, y);
        doc.text("Preguntas", 100, y);
        doc.text("Correctas", 130, y);
        doc.text("% Aciertos", 160, y);
        y += 5;

        doc.setDrawColor(200, 200, 200);
        doc.line(20, y, 190, y);
        y += 5;

        doc.setFont("helvetica", "normal");
        finalizadas.forEach((sim) => {
          if (y > 250) {
            doc.addPage();
            y = 20;
          }
          doc.text(new Date(sim.fecha).toLocaleDateString("es-ES"), 20, y);
          doc.text(sim.estado, 60, y);
          doc.text(sim.totalPreguntas.toString(), 100, y);
          doc.text(sim.correctas.toString(), 130, y);
          doc.text(`${sim.porcentajeCorrectas.toFixed(1)}%`, 160, y);
          y += 7;
        });
      }
    } else {
      doc.text("No hay simulaciones registradas para este alumno.", 20, y);
    }
  };

  const tiposReporte = [
    {
      id: "alumnos" as TipoReporte,
      nombre: "Reporte de Alumnos",
      descripcion: "Lista completa de alumnos con sus datos y estado",
      icono: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
    },
    {
      id: "cursos" as TipoReporte,
      nombre: "Reporte de Cursos",
      descripcion: "Información detallada de todos los cursos asignados",
      icono: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
    },
    {
      id: "simulaciones" as TipoReporte,
      nombre: "Reporte de Simulaciones",
      descripcion: "Análisis completo de simulaciones PAES realizadas",
      icono: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      id: "progreso-general" as TipoReporte,
      nombre: "Progreso General",
      descripcion: "Vista general del progreso de todos los alumnos",
      icono: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      id: "rendimiento-alumno" as TipoReporte,
      nombre: "Rendimiento por Alumno",
      descripcion: "Análisis detallado del rendimiento de un alumno específico",
      icono: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
    },
  ];

  return (
    <ProfesorLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header mejorado */}
        <div className="mb-10">
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl shadow-lg p-8 text-white">
            <h1 className="text-4xl font-bold mb-2">Generador de Reportes</h1>
            <p className="text-purple-100 text-lg">
              Crea reportes profesionales en PDF con toda la información que
              necesitas
            </p>
          </div>
        </div>

        {/* Selección de tipo de reporte */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg
              className="w-6 h-6 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Selecciona el Tipo de Reporte
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tiposReporte.map((tipo) => (
              <button
                key={tipo.id}
                onClick={() => setTipoReporte(tipo.id)}
                className={`p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                  tipoReporte === tipo.id
                    ? "border-purple-600 bg-purple-50 shadow-lg scale-105"
                    : "border-gray-200 bg-white hover:border-purple-300 hover:shadow-md"
                }`}
              >
                <div
                  className={`mb-4 ${
                    tipoReporte === tipo.id
                      ? "text-purple-600"
                      : "text-gray-400"
                  }`}
                >
                  {tipo.icono}
                </div>
                <h3
                  className={`text-lg font-bold mb-2 ${
                    tipoReporte === tipo.id
                      ? "text-purple-900"
                      : "text-gray-900"
                  }`}
                >
                  {tipo.nombre}
                </h3>
                <p className="text-sm text-gray-600">{tipo.descripcion}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white shadow-xl rounded-xl p-6 mb-8 border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filtros Opcionales
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Desde
              </label>
              <input
                type="date"
                value={filtros.fechaDesde || ""}
                onChange={(e) =>
                  setFiltros({ ...filtros, fechaDesde: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={filtros.fechaHasta || ""}
                onChange={(e) =>
                  setFiltros({ ...filtros, fechaHasta: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            {tipoReporte === "rendimiento-alumno" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alumno
                </label>
                <select
                  value={filtros.alumnoId || ""}
                  onChange={(e) =>
                    setFiltros({
                      ...filtros,
                      alumnoId: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Selecciona un alumno</option>
                  {alumnos.map((alumno) => (
                    <option key={alumno.idUsuario} value={alumno.idUsuario}>
                      {alumno.nombreCompleto}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {tipoReporte !== "rendimiento-alumno" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Curso
                </label>
                <select
                  value={filtros.cursoId || ""}
                  onChange={(e) =>
                    setFiltros({
                      ...filtros,
                      cursoId: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Todos los cursos</option>
                  {cursos.map((curso) => (
                    <option key={curso.idCurso} value={curso.idCurso}>
                      {curso.nombreCompleto}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Botón de generar */}
        <div className="flex justify-center">
          <button
            onClick={generarPDF}
            disabled={
              generando ||
              (tipoReporte === "rendimiento-alumno" && !filtros.alumnoId)
            }
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-3"
          >
            {generando ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Generando PDF...
              </>
            ) : (
              <>
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Generar Reporte PDF
              </>
            )}
          </button>
        </div>

        {/* Vista previa de datos */}
        {loading ? (
          <div className="mt-8 text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Cargando datos...</p>
          </div>
        ) : (
          <div className="mt-8 bg-white shadow-xl rounded-xl p-6 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Vista Previa de Datos
            </h2>
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                <span className="font-semibold">Alumnos disponibles:</span>{" "}
                {alumnos.length}
              </p>
              <p>
                <span className="font-semibold">Cursos disponibles:</span>{" "}
                {cursos.length}
              </p>
              <p>
                <span className="font-semibold">Simulaciones disponibles:</span>{" "}
                {simulaciones.length}
              </p>
            </div>
          </div>
        )}
      </div>
    </ProfesorLayout>
  );
};
